// Seletores da jornada guiada do Fee Fixo — a metade que o backend não pode ter.
//
// O TEXTO de cada micro-passo vem do catálogo da Jornada
// (`metadata.instructions[]`, servido pelo backend) e é casado com esta lista
// POR ÍNDICE. Aqui só moram os seletores: o backend não conhece o DOM do front,
// e amarrar um `[data-tour]` na curadoria faria qualquer refactor de template
// virar deploy de backend.
//
// ★ Este arquivo É o escopo do tour. A etapa seguinte só é oferecida ao usuário
// se tiver entrada aqui — não existe uma lista paralela de "etapas do v1" para
// manter em dia (duas listas divergem na primeira mudança). Guiar uma etapa
// nova é acrescentar uma chave.
//
// Convenções:
//   alvo             seletor `[data-tour="ff-*"]`. `null` = passo sem elemento
//                    ancorável — EXIGE `semAlvo` com o motivo (o teste cobra).
//   avancaNoClique   ★ o DEFAULT PRÁTICO é `false`. É `true` quando clicar no
//                    alvo CONCLUI o passo — e a pergunta que decide é o que o
//                    clique faz com o passo SEGUINTE:
//                      · ATRAPALHA  ⇒ false. Passo que só LOCALIZA ("o botão
//                        fica no canto superior direito") — clicar ali abre o
//                        modal, e o passo seguinte apontaria algo que o modal
//                        acabou de cobrir. Apontar não é acionar.
//                      · HABILITA   ⇒ true. Passo cuja instrução É a ação, e o
//                        passo seguinte PRECISA do que ela produziu. "Abra o
//                        importador" é isso: o passo 2 acontece dentro do modal
//                        que este clique abre.
//                    Os dois casos foram vistos em tela em 14/08 — o segundo
//                    porque o passo 1 herdou `false` do roteiro antigo e ficava
//                    repetindo "abra o importador" com o importador já aberto,
//                    sem apontar nada (o alvo dele fica coberto pelo modal).
//   aguarda          canal do GlobalBus que faz o alvo existir (modal). O retry
//                    padrão do host é de ~2,4s, curto demais para "o usuário
//                    abre o modal, lê e escolhe o arquivo".
import { MODAL } from '@constants/Text'

// De-para entre a chave `modal` do catálogo e o canal real do GlobalBus.
// Mapa explícito, nunca derivação por string: `'modal_' + chave` casaria hoje
// por coincidência, e uma terceira chave que não siga o padrão resolveria para
// um canal que ninguém escuta — sem erro em tela nem no console.
export const CATALOGO_MODAL_PARA_CANAL = {
  fee_fixo_importar_clientes: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
  // A janela "O que entra no relatório", segundo estágio de gerar o PDF.
  fee_fixo_secoes_relatorio: MODAL.FEE_FIXO_SECOES_RELATORIO,
  // Simulação em lote — o que a barra de ações da lista dispara.
  fee_fixo_simulacao_batch: MODAL.FEE_FIXO_SIMULACAO_BATCH,
  // Nível 2, fora do escopo do v1 (sem entrada em PASSOS_DO_TOUR). Fica aqui
  // porque é grátis e documenta o gancho `action: 'modal'` do catálogo.
  fee_fixo_salvar_analise: MODAL.FEE_FIXO_SALVAR_ANALISE,
  // ★ 20/08/2026 (spec-006, [T008]) — o modal unificado (Relatório e
  // Apresentação). O passo `gerar-relatorio-cliente` espera por ele — ver o
  // comentário no próprio passo, mais abaixo.
  fee_fixo_gerar_apresentacao: MODAL.FEE_FIXO_GERAR_APRESENTACAO,
}

export const PASSOS_DO_TOUR = {
  // ★ Esta etapa é a porta de entrada de quem nunca usou a plataforma, e desde
  // 14/08 ela assume que o usuário NÃO tem arquivo nenhum: o caminho principal
  // é baixar o modelo da AAWZ, preencher e subir esse mesmo arquivo. Quem já
  // exporta o Positivador da XP ou a Posição do BTG tem o atalho dito no texto
  // do passo 2. Antes, o primeiro passo mandava "usar o arquivo da corretora,
  // sem reformatar nada" — instrução que só serve a quem já tinha o relatório
  // em mãos, e que deixava sem saída justamente quem está começando.
  //
  // ⚠️ NENHUM passo COM ALVO desta etapa avança no clique. Os que apontam o
  // split button apenas LOCALIZAM — clicar ali abre o modal de importação, e o
  // tour não pode tratar isso como "passo concluído": o passo seguinte fala de
  // algo que o modal cobriu. Os passos do modelo e da dropzone também não
  // avançam, porque englobam uma ação que termina FORA do browser (baixar,
  // preencher, salvar); quem diz que terminou é o usuário, no "Próximo".
  // O passo sem alvo fica no default (`avancaNoClique` ausente) como os outros
  // `semAlvo` do arquivo: sem elemento, não há clique a escutar.
  'trazer-base-clientes': [
    // "Abra o importador" — o split button do canto superior direito. AVANÇA no
    // clique: abrir o modal é a ação que o passo pede, e é o que habilita o
    // passo 2, que mora dentro dele. Sem isso o tour ficava preso repetindo
    // "abra o importador" com a janela já aberta e sem furo nenhum, porque o
    // próprio modal cobre o botão que o passo aponta.
    //
    // `abreNoProximo`: quem clica em "Próximo" sem ter aberto a janela é levado
    // — o tour abre por ele. É abertura de modal, dentro do escopo permitido
    // (idempotente, sem efeito no servidor). Sem isto, "Próximo" levava ao passo
    // 2, que fala de um botão dentro de uma janela ainda fechada, e daí em
    // diante cada passo descrevia uma tela que não estava na frente do usuário.
    { alvo: '[data-tour="ff-importar-split"]', abreNoProximo: true },
    // "Baixe o modelo da AAWZ" — o botão vive DENTRO do modal, logo acima da
    // dropzone. Mesmo par de alvos da dropzone abaixo: fechado, aponta o botão
    // que abre a janela; aberta, o furo pula para o "baixar o modelo".
    {
      alvo: '[data-tour="ff-importar-split"]',
      alvoAoAbrir: '[data-tour="ff-import-modelo"]',
      aguarda: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
      avancaNoClique: false,
    },
    // "Suba a planilha aqui" — a dropzone, também dentro do modal.
    //
    // NÃO há passo de "preencha a planilha" entre baixar e subir (decisão do
    // owner, 14/08): quem está começando pode subir o próprio modelo que acabou
    // de baixar e ver o fluxo inteiro funcionar; preencher de verdade é o
    // segundo uso, não o primeiro. Um passo pedindo trabalho fora do browser
    // trava o tour justamente em quem ele existe para ajudar.
    //
    // Escolher o arquivo termina FORA do browser (o seletor do sistema, ou o
    // arrastar), mas o componente recebe o arquivo pelo `@input` e publica o
    // evento abaixo. Enquanto isso não acontecer, o card troca "Próximo" pela
    // instrução de espera; assim o tour não descreve a validação com a dropzone
    // ainda vazia.
    //
    // ⚠️ LACUNA CONHECIDA (21/09/2026): o ouvinte do `avancaEm` só existe
    // enquanto ESTE passo está ativo (`ouvirCanalDeAvanco`, religado por passo
    // em `central-config-tour-guiado.vue`). A dropzone, porém, já está clicável
    // no passo anterior — o véu do overlay é `pointer-events: none`. Quem já
    // tem a planilha em mãos e a solta antes de chegar aqui emite o sinal sem
    // ouvinte, e então encontra um passo SEM "Próximo" pedindo uma ação que já
    // fez; a saída é limpar e reescolher, que o balão não conta. Vale para os
    // três `avancaEm` do módulo, e o conserto é do host — um predicado de
    // "passo já satisfeito", que devolve o "Próximo" quando a pré-condição já
    // está atendida —, não deste mapa.
    {
      alvo: '[data-tour="ff-importar-split"]',
      alvoAoAbrir: '[data-tour="ff-import-upload"]',
      aguarda: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
      avancaNoClique: false,
      avancaEm: MODAL.FEE_FIXO_ARQUIVO_CLIENTES_SELECIONADO,
    },
    // ★ Os três passos abaixo desdobram o que a janela faz DEPOIS do arquivo
    // (achado do owner em tela, 14/08). O roteiro pulava da dropzone direto para
    // "traga a carteira", e o balão aparecia mandando FECHAR a janela enquanto
    // ela estava aberta, com um "Continuar" intocado na frente do usuário: o
    // tour descrevia o depois da importação no meio dela.
    //
    // ⚠️ Um balão por CLIQUE, não um balão para o processo inteiro. A versão que o
    // owner reprovou juntava quatro ações num texto só ("feche a janela, abra a
    // seta, baixe o modelo da carteira, importe") — ninguém segue quatro
    // instruções de um balão de seis linhas. Se um passo precisa de "e depois",
    // ele é dois passos.

    // "Clique em Continuar" — valida o arquivo e vira a prévia.
    //
    // `abreNoProximo` é legítimo aqui: `validarImportacao` manda `save: false`,
    // então o clique LÊ a planilha e não grava nada. É o mesmo critério do passo
    // 1 (sem efeito no servidor), e não vale para o "Confirmar" logo abaixo, que
    // grava de verdade.
    {
      alvo: '[data-tour="ff-importar-split"]',
      alvoAoAbrir: '[data-tour="ff-import-continuar"]',
      aguarda: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
      abreNoProximo: true,
    },
    // "Confira a prévia" — a tabela do que a plataforma entendeu. É leitura, e
    // é o passo que existe para o usuário OLHAR antes de gravar: conta,
    // patrimônio e data de nascimento saindo trocados da planilha é o erro caro
    // desta etapa, e depois do "Confirmar" ele já está na base.
    {
      alvo: '[data-tour="ff-importar-split"]',
      alvoAoAbrir: '[data-tour="ff-import-preview"]',
      aguarda: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
      avancaNoClique: false,
    },
    // "Confirme a importação" — o único clique desta etapa que GRAVA, e por isso
    // o único que o "Próximo" não faz por ninguém.
    //
    // `avancaEm`: confirmar não termina no clique — termina quando a importação
    // grava, e quem sabe disso é a tela, que já emitia este canal para recarregar
    // a lista. O tour só escuta.
    {
      alvo: '[data-tour="ff-importar-split"]',
      alvoAoAbrir: '[data-tour="ff-import-confirmar"]',
      aguarda: MODAL.FEE_FIXO_IMPORTAR_CLIENTES,
      avancaEm: MODAL.FEE_FIXO_RELOAD_CLIENTES,
      avancaNoClique: false,
    },
    // ⚠️ NÃO existe mais passo de "traga a carteira" (decisão do owner, 14/08).
    // Era opcional pelo próprio texto, pedia quatro ações num balão só, e ficava
    // no MEIO do fluxo de importar clientes — outro assunto, na hora errada. A
    // informação sobrevive como uma frase no passo de fechamento; se a carteira
    // virar requisito, ela merece etapa própria no catálogo, não um passo
    // enxertado nesta.
    //
    // "Confira o que entrou" — leitura dos indicadores, não é ação.
    { alvo: '[data-tour="ff-clientes-recorte"]', avancaNoClique: false },
  ],

  'priorizar-clientes-com-ia': [
    { alvo: '[data-tour="ff-ia-filtros"]', avancaNoClique: false },
    // Clicar no cabeçalho EXPANDE a seção — é a ação do passo, e o passo
    // seguinte (o campo) só existe expandido. Aqui avançar no clique é certo, e
    // pela mesma razão o "Próximo" pode expandir por quem não expandiu: sem
    // isso o passo seguinte aponta um campo que ainda não está na tela.
    { alvo: '[data-tour="ff-ia-header"]', abreNoProximo: true },
    { alvo: '[data-tour="ff-ia-campo"]', avancaNoClique: false },
    // "Confira o filtro sugerido" — o preview só NASCE quando a IA responde.
    //
    // ⚠️ Apontava direto `ff-ia-preview`: até o usuário escrever e clicar em
    // "Montar filtro", o seletor não resolvia nada e o passo ficava sem furo
    // (medido em tela, 14/08). Com o par, o furo fica no campo — onde ele
    // precisa agir — e salta para o preview assim que ele aparece.
    {
      alvo: '[data-tour="ff-ia-campo"]',
      alvoAoAbrir: '[data-tour="ff-ia-preview"]',
      semAguarda:
        'o preview nasce da resposta da IA, que não emite canal do GlobalBus; quem pega a inserção é o MutationObserver do host',
      avancaNoClique: false,
    },
    { alvo: '[data-tour="ff-clientes-recorte"]', avancaNoClique: false },
  ],

  'simular-migracao-cliente': [
    // O passo 1 do catálogo entra pela aba Clientes ("clique em Simular na
    // linha"), mas a etapa navega para /fee-fixo/simulacao — o próprio detalhe
    // dá a alternativa que vale aqui: "abra Simulação e use 'Selecione um
    // cliente'". Ancorar no seletor mantém o balão honesto com a tela que o
    // usuário está vendo. Não avança no clique: escolher o cliente abre um
    // dropdown, e o passo seguinte ainda fala do mesmo seletor.
    { alvo: '[data-tour="ff-sim-cliente"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-cliente"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-premissas"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-regimes"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-cenarios"]' },
  ],

  'guardar-cenario-cliente': [
    { alvo: '[data-tour="ff-sim-cliente"]', avancaNoClique: false },
    // Renomear abre um menu de três pontos — apontar, não acionar.
    { alvo: '[data-tour="ff-sim-cenarios"]', avancaNoClique: false },
    // Este SIM: clicar em "Guardar cenário" salva, e o passo seguinte é
    // justamente conferir a mensagem que o salvamento produz.
    { alvo: '[data-tour="ff-sim-guardar"]' },
    {
      alvo: null,
      semAlvo:
        'a confirmação é um snack que aparece e some sozinho — não há elemento estável para o furo; o balão fica centralizado',
    },
  ],

  // ★ As duas etapas abaixo entraram em 14/08, quando o escritório subiu para o
  // nível 2 e elas apareceram como obrigatórias sem tour — o card oferecia
  // "Iniciar etapa" e o usuário caía no drawer de texto, sem spotlight.
  'adicionar-clientes-pipeline': [
    // "Continue na tela da simulação" — o botão fica no header, ao lado de
    // "Gerar relatório". Não avança no clique: o passo seguinte é confirmar
    // dentro do modal que este clique abre.
    { alvo: '[data-tour="ff-sim-pipeline"]', avancaNoClique: false },
    // "Confirme a oportunidade" — o modal é do CRM (reaproveitado, ver §7 do
    // CLAUDE.md do módulo), então o alvo não é um `data-tour` do fee-fixo: sem
    // âncora no componente de terceiro, o balão fica centralizado sobre o modal
    // aberto, que já é a única coisa na tela.
    {
      alvo: null,
      semAlvo:
        'o modal de oportunidade é do CRM e não carrega âncora do fee-fixo; aberto, ele já ocupa a tela sozinho e o balão centralizado não compete com nada',
    },
    // "Para vários clientes de uma vez" — a barra de lote da aba Clientes, que
    // só existe com linha marcada. Sem seleção, ancora na tabela: é onde o
    // usuário precisa agir primeiro (marcar o checkbox).
    {
      alvo: '[data-tour="ff-clientes-tabela"]',
      alvoAoAbrir: '[data-tour="ff-clientes-lote"]',
      aguarda: MODAL.FEE_FIXO_SIMULACAO_BATCH,
      avancaNoClique: false,
    },
  ],

  'ensaiar-conversa-roleplay': [
    // "Abra a aba Clientes" — a lista é o ponto de partida do ensaio.
    { alvo: '[data-tour="ff-clientes-tabela"]', avancaNoClique: false },
    // "Abra o ensaio do cliente" — a ação vive no menu de três pontos da linha
    // (⚠️ o texto do catálogo ainda fala em "microfone da linha", que deixou de
    // existir em 12/08/2026 quando os três ícones viraram `BaseActionsMenu`).
    { alvo: '[data-tour="ff-clientes-tabela"]', avancaNoClique: false },
    // "Monte o personagem" — o seletor de tom, já na tela do ensaio.
    { alvo: '[data-tour="ff-ensaio-tom"]', avancaNoClique: false },
    // "Escolha o modo" — voz ou texto.
    { alvo: '[data-tour="ff-ensaio-modo"]', avancaNoClique: false },
    // "Encerre e veja a nota" — o botão só existe com a SESSÃO aberta, e quem o
    // faz aparecer é o próprio usuário começando o ensaio, não um canal de
    // modal: `aguarda` aqui seria mentira (o canal da avaliação só dispara
    // DEPOIS de encerrar, tarde demais para ancorar o botão que encerra).
    // Enquanto a sessão não começa o balão centraliza, e o observador de DOM
    // reancora sozinho no instante em que o botão entra na tela.
    // "Encerre e veja a nota" — o botão só existe DURANTE uma sessão ativa.
    //
    // ⚠️ Apontava direto `ff-ensaio-encerrar`: antes de a conversa começar ele
    // não está no DOM, e o passo ficava sem furo (medido em tela, 14/08). O
    // alvo base é o seletor de modo, que fica na tela o tempo inteiro.
    {
      alvo: '[data-tour="ff-ensaio-modo"]',
      alvoAoAbrir: '[data-tour="ff-ensaio-encerrar"]',
      semAguarda:
        'o botão de encerrar aparece quando a sessão do ensaio começa, sem canal do GlobalBus para anunciar; o MutationObserver do host cobre',
      avancaNoClique: false,
    },
  ],

  // ★ As 6 etapas abaixo fecharam a cobertura em 14/08: o módulo tem 13 etapas
  // no catálogo e 7 tinham tour. As que faltavam eram todas de níveis à frente
  // (pipeline, proposta, premissas) — invisíveis enquanto o escritório estava no
  // nível 1, e sem guia no dia em que ele sobe.
  'acompanhar-evolucao-migracao': [
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    // "Veja o efeito no agregado" — os KPIs vivem na Visão Geral, outra aba.
    //
    // ⚠️ Apontava `ff-dash-kpis`, que só existe em /fee-fixo/dashboard. Esta
    // etapa roda em /fee-fixo/pipeline, então o furo não aparecia e o passo
    // encerrava a etapa apontando o nada (medido em tela, 14/08). O texto já
    // diz para abrir a "Visão Geral"; sem alvo, o balão centraliza e a
    // instrução se sustenta sozinha.
    {
      alvo: null,
      semAlvo:
        'os KPIs de "% AuC em Fee Fixo" vivem na aba Visão Geral, e esta etapa roda no Acompanhamento — não há elemento desta tela a destacar',
    },
  ],

  'apresentar-proposta-migracao': [
    { alvo: '[data-tour="ff-sim-cliente"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-regimes"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-graficos"]', avancaNoClique: false },
    // "Registre o combinado na oportunidade" — a edição acontece no modal do
    // CRM, aberto pelo lápis do card no Acompanhamento.
    //
    // ⚠️ Apontava `ff-pipeline-kanban`, que vive em OUTRA tela
    // (/fee-fixo/pipeline). Esta etapa roda em /fee-fixo/simulacao, então o
    // seletor não resolvia nada: o passo ficava sem furo e o cursor sumia, com o
    // balão descrevendo uma tela que não estava na frente do usuário (medido em
    // tela, 14/08). O texto já manda ir para "Acompanhamento" — é instrução de
    // navegação, e para isso o balão centralizado é honesto.
    {
      alvo: null,
      semAlvo:
        'a oportunidade é editada em "Acompanhamento", outra aba, e num modal do CRM sem âncora do fee-fixo; daqui não há elemento a apontar',
    },
  ],

  'avancar-etapas-pipeline': [
    { alvo: '[data-tour="ff-pipeline-modo"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
  ],

  'registrar-desfecho-migracao': [
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    // "Confira o indicador de conversão" — os KPIs do funil ficam no topo da
    // própria aba, dentro do mesmo bloco do quadro.
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
  ],

  'acompanhar-onboarding-pos-conversao': [
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    // Os nove pontos do onboarding são uma LISTA no texto do passo — não há
    // tela de checklist na plataforma (o próprio detalhe da etapa diz isso).
    {
      alvo: null,
      semAlvo:
        'os nove pontos do onboarding não têm tela: o próprio texto da etapa diz que o registro é feito em tarefas e observações, então não há elemento a apontar',
    },
    { alvo: '[data-tour="ff-pipeline-kanban"]', avancaNoClique: false },
    {
      alvo: null,
      semAlvo:
        'a cadência por segmento também não tem tela — é combinado de time registrado no CRM, fora do fluxo desta etapa',
    },
  ],

  // ⚠️ O catálogo aponta esta etapa para /fee-fixo/simulacao, mas a tela de
  // premissas do ESCRITÓRIO é /fee-fixo/configuracoes — que é gestor-only desde
  // 06/08 (ver §11). A divergência é de curadoria e está registrada no §18; o
  // mapa segue a URL que o catálogo manda, ancorando no painel de premissas da
  // Simulação, que é o que o texto dos passos descreve.
  'definir-premissas-escritorio': [
    { alvo: '[data-tour="ff-sim-premissas"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-premissas"]', avancaNoClique: false },
    { alvo: '[data-tour="ff-sim-premissas"]', avancaNoClique: false },
    {
      alvo: null,
      semAlvo:
        'não há onde salvar premissa no nível do escritório — o próprio passo manda anotar fora da plataforma, então não existe elemento a destacar',
    },
    {
      alvo: null,
      semAlvo:
        'o aviso de que a etapa vale para o time inteiro é sobre o efeito da marcação, não sobre um elemento da tela',
    },
  ],

  'gerar-relatorio-cliente': [
    { alvo: '[data-tour="ff-sim-cliente"]', avancaNoClique: false },
    // ★ 20/08/2026 (spec-006, unificar-relatorio-e-apresentacao) — realinhado
    // depois do T008: o botão dedicado "Gerar relatório" saiu da barra
    // (US-4), e o modelo Relatório agora nasce do modal unificado
    // (`modal-gerar-apresentacao`), que devolve o mesmo sinal de conclusão
    // que o relatório sempre emitiu (`MODAL.FEE_FIXO_RELATORIO_GERADO` — o
    // modelo Relatório reaproveita o polling por hash de sempre, não o card
    // por `request_id`; ver `view-fee-fixo-simulacao-apresentacao.js`).
    // `ff-sim-relatorio` é hoje o `data-tour` do botão "Gerar apresentação"
    // (mesmo atributo, dono novo); `ff-sim-relatorio-confirmar` mudou de
    // `modal-secoes-relatorio.vue` (que não abre mais — sem gatilho de UI)
    // para o confirmar do modal unificado.
    //
    // ⚠️ Lacuna que PERMANECE, e não é técnica: o modal abre com o modelo
    // Apresentação pré-selecionado (o default de sempre — spec-006 decidiu
    // não regredir o comportamento existente). O tour não tem como forçar a
    // escolha de "Relatório" — ele só aponta e espera, nunca emite dentro de
    // um modal (mesma regra do passo `trazer-base-clientes`). Quem seguir o
    // balão sem trocar o seletor de modelo gera uma Apresentação, não um
    // Relatório. Fica registrado aqui e no CLAUDE.md §18: é decisão de
    // produto/conteúdo (texto do catálogo, curado pelo backend), não algo
    // que este arquivo resolve sozinho.
    {
      alvo: '[data-tour="ff-sim-relatorio"]',
      alvoAoAbrir: '[data-tour="ff-sim-relatorio-confirmar"]',
      aguarda: MODAL.FEE_FIXO_GERAR_APRESENTACAO,
      // O PDF passa por duas Lambdas e pode levar até 2 minutos: o passo não
      // acaba no clique, acaba quando o arquivo baixa. Quem sabe é a tela.
      avancaEm: MODAL.FEE_FIXO_RELATORIO_GERADO,
      avancaNoClique: false,
    },
    {
      alvo: null,
      semAlvo:
        'a conferência é sobre o PDF já baixado, fora da aplicação; o balão fica centralizado',
    },
  ],
}

// Slug do módulo no catálogo. Precisa bater com o `slug` do backend — divergir
// faz o lookup devolver null e o tour sumir em silêncio (mesma armadilha do
// `moduleKey` do card da Home e do CTA da landing).
export const MODULE_KEY_FEE_FIXO = 'fee-fixo'

export function temTourGuiado(slug) {
  if (!slug) return false
  return Object.hasOwn(PASSOS_DO_TOUR, slug)
}
